import type Phaser from 'phaser';
import type { RoundEndReason } from './ko-presentation-model';
import { KO_THEMES } from './ko-presentation-model';

export class KoAnnouncementOverlay {
  public readonly container: Phaser.GameObjects.Container;
  private readonly dimGraphics: Phaser.GameObjects.Graphics;
  private readonly decoGraphics: Phaser.GameObjects.Graphics;
  private readonly mainLabel: Phaser.GameObjects.Text;
  private readonly subLabel: Phaser.GameObjects.Text;

  public constructor(private readonly scene: Phaser.Scene) {
    this.container = scene.add.container(0, 0).setDepth(130).setVisible(false);

    // Dim background
    this.dimGraphics = scene.add.graphics();

    // Subtle decorative accents
    this.decoGraphics = scene.add.graphics();

    // Main K.O. / TIME UP / DRAW text
    this.mainLabel = scene.add.text(640, 290, '', {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '92px',
      fontStyle: 'bold',
      align: 'center',
    }).setOrigin(0.5);

    // Subtitle text (e.g. INCIDENT CLOSED)
    this.subLabel = scene.add.text(640, 360, '', {
      fontFamily: '"Helvetica Neue", "Segoe UI", "Hiragino Kaku Gothic ProN", "Meiryo", system-ui, sans-serif',
      fontSize: '14px',
      fontStyle: 'bold',
      align: 'center',
    }).setOrigin(0.5);

    this.container.add([
      this.dimGraphics,
      this.decoGraphics,
      this.mainLabel,
      this.subLabel,
    ]);
  }

  public show(reason: RoundEndReason): void {
    const theme = KO_THEMES[reason];
    this.container.setVisible(true).setAlpha(1);

    // Render dim if enabled
    this.dimGraphics.clear();
    if (theme.hasDim && theme.dimAlpha > 0) {
      this.dimGraphics.fillStyle(0x000000, theme.dimAlpha);
      this.dimGraphics.fillRect(0, 0, 1280, 720);
    }

    // Render decorative accents
    this.decoGraphics.clear();
    if (reason === 'KO') {
      const cx = 640;
      const cy = 360;
      // Accent line flanking the subtitle
      this.decoGraphics.lineStyle(1.5, 0xef4444, 0.7);
      this.decoGraphics.lineBetween(cx - 160, cy, cx - 80, cy);
      this.decoGraphics.lineBetween(cx + 80, cy, cx + 160, cy);
    }

    // Configure text
    this.mainLabel.setText(theme.text);
    this.mainLabel.setFontSize(theme.fontSize);
    this.mainLabel.setColor(theme.textColor);
    this.mainLabel.setStroke(theme.strokeColor, theme.strokeThickness);

    this.subLabel.setText(theme.subText);
    this.subLabel.setColor(theme.subColor);

    // Animate scale-in and fade-in
    this.mainLabel.setScale(1.35).setAlpha(0);
    this.subLabel.setAlpha(0);
    this.decoGraphics.setAlpha(0);

    this.scene.tweens.add({
      targets: this.mainLabel,
      scaleX: 1,
      scaleY: 1,
      alpha: 1,
      duration: 160,
      ease: 'Back.Out',
    });

    this.scene.tweens.add({
      targets: [this.subLabel, this.decoGraphics],
      alpha: 1,
      duration: 180,
      ease: 'Cubic.Out',
    });

    // Camera shake if configured
    if (theme.cameraShake !== null) {
      this.scene.cameras.main.shake(theme.cameraShake.durationMs, theme.cameraShake.intensity);
    }
  }

  public dismiss(): void {
    this.container.setVisible(false);
    this.dimGraphics.clear();
    this.decoGraphics.clear();
  }

  public get isVisible(): boolean {
    return this.container.visible;
  }
}
